import { DefaultTheme } from 'styled-components';
import { StyledIcon } from 'components/elements/Icon/Icon.styled';
import { css, styled } from 'styles';
import { ButtonTheme } from 'types/styles';
import { ButtonProps, ButtonSize, ButtonVariant } from './types';

const normalStyle = css`
  font-size: 1rem;
  height: ${({ theme }) => theme.element.height}px;
  padding: 0 ${({ theme }) => theme.spacing.relative.medium};

  ${StyledIcon} {
    font-size: 1.4em;
  }
`;

const smallStyle = css`
  font-size: 0.95rem;
  height: 2rem;
  padding: 0 ${({ theme }) => theme.spacing.relative.small};

  ${StyledIcon} {
    font-size: 1.3em;
  }
`;

const createVariantStyle = (config: ButtonTheme, active?: boolean) => css`
  background-color: ${active ? config.hover.background : config.background};
  color: ${config.color};

  &:active:not(:disabled),
  &:hover:not(:disabled) {
    background-color: ${config.hover.background};
  }

  &:disabled {
    background-color: ${config.disabled.background};
    color: ${config.disabled.color};
  }
`;

const getSizeStyle = (theme: DefaultTheme, size?: ButtonSize) => {
  switch (size) {
    case 'small':
      return smallStyle;
    case 'normal':
    default:
      return normalStyle;
  }
};

const getVariantStyle = (theme: DefaultTheme, variant?: ButtonVariant, active?: boolean) => {
  switch (variant) {
    case 'negative':
      return createVariantStyle(theme.button.negative, active);
    case 'positive':
      return createVariantStyle(theme.button.positive, active);
    case 'primary':
      return createVariantStyle(theme.button.primary, active);
    case 'transparent':
      return createVariantStyle(theme.button.transparent, active);
    default:
      return createVariantStyle(theme.button.default, active);
  }
};

export const StyledButton = styled.button<ButtonProps>`
  align-items: center;
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  line-height: ${({ theme }) => theme.element.lineHeight};
  outline: none;
  transition: background-color ${({ theme }) => theme.transition.medium} ease-in-out,
    color ${({ theme }) => theme.transition.medium} ease-in-out,
    opacity ${({ theme }) => theme.transition.medium} ease-in-out;
  user-select: none;

  &:disabled {
    cursor: default;
  }

  ${({ active, theme, variant }) => getVariantStyle(theme, variant, active)};
  ${({ size, theme }) => getSizeStyle(theme, size)};
`;

StyledButton.displayName = 'Button.Styled';
